import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography } from "@material-ui/core";
import CustomDialog from "../common/Dialog";
import { useStyles } from "./notificationCentr.styles";
import { useSelector, useDispatch } from "react-redux";
import { AppState } from "config/redux/reducers";
import {
  fetchMessageFromCenter,
  ackMessage,
} from "./redux/notificationCenterSlice";
import NotificationList from "./NotificationList";
import TabsSection from "./NotificationTabs";
import { NotificatioResponse } from "./redux/notificationCenterSlice.types";
import { NotificationCenterConst } from "../constants/NotificationConstant";
import { TextConstants } from "constants/TextConstants";
import { createNotificationPayload } from "./response.transforms";

interface NotficationdDialogProps {
  openNotificationCenter: boolean;
  handleDialogClose: () => void;
}

const NotificationCenterDialog = ({
  openNotificationCenter,
  handleDialogClose,
}: NotficationdDialogProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { countryCode } = useSelector((state: AppState) => state.common);
  const { messages } = useSelector(
    (state: AppState) => state.notificationCenter
  );

  const [notifications, setNotifications] = useState<NotificatioResponse[]>([]);
  const [activeTab, setActiveTab] = useState(0);
  const [tooltipOpen, setTooltipOpen] = useState<string | null>(null);

  // Fetch notifications on mount and at regular intervals
  useEffect(() => {
    const fetchNotifications = () => {
      const payload = createNotificationPayload(NotificationCenterConst.DEFAULT_FILTERS ,countryCode, activeTab);
      dispatch(
        fetchMessageFromCenter({
          filters: payload,
        })
      );
    };
  
    // Fetch notifications immediately on mount or when dependencies change
    fetchNotifications();
  
    // Set up interval for periodic fetching
    const intervalId = setInterval(fetchNotifications, NotificationCenterConst.FETCH_INTERVAL);
  
    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [dispatch, countryCode, activeTab]);

  // Update notifications when messages change
  useEffect(() => {
    if (messages?.elements) {
      setNotifications(messages.elements);
    }
  }, [messages]);

  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  // Mark notification as read
  const markAsRead = (id: string) => {
    dispatch(ackMessage(id))
      .then(() => {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) =>
            notification.messageId === id
              ? { ...notification, ack: true }
              : notification
          )
        );
      })
      .catch((error: any) => {
        // eslint-disable-next-line no-console
        console.error("Failed to mark notification as read:", error);
      });
  };

    // Mark all notification as read
  const markAllAsRead = () => {
    dispatch(ackMessage(undefined)) // Dispatch without an ID
      .then(() => {
        setNotifications((prevNotifications) =>
          prevNotifications.map((notification) => ({
            ...notification,
            ack: true,
          }))
        );
      })
      .catch((error: any) => {
        // eslint-disable-next-line no-console
        console.error("Failed to mark all notifications as read:", error);
      });
  };

  // Copy text to clipboard
  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setTooltipOpen(id);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to copy to clipboard:", error);
    }
  };

  // Filter notifications based on the active tab
  const filteredNotifications = useMemo(() => {
    return activeTab === 0
      ? notifications.filter((notification) => !notification.ack)
      : notifications.filter((notification) => notification.ack);
  }, [activeTab, notifications]);

  const getTitleContent = () => (
    <Typography className={classes.Heading}>
      {TextConstants.NOTIFICATION}
    </Typography>
  );

  return (
    <CustomDialog
      open={openNotificationCenter}
      handleClose={handleDialogClose}
      PaperProps={{
        classes: { root: classes.dialogPaperPropsRoot },
      }}
      title={getTitleContent()}
      content={
        <Box>
          <TabsSection
            activeTab={activeTab}
            notifications={notifications}
            onTabChange={handleTabChange}
            onMarkAllAsRead={markAllAsRead}
          />
          <NotificationList
            notifications={filteredNotifications}
            onMarkAsRead={markAsRead}
            onCopyToClipboard={copyToClipboard}
            tooltipOpen={tooltipOpen}
            setTooltipOpen={setTooltipOpen}
          />
        </Box>
      }
      actions={null}
    />
  );
};
export default NotificationCenterDialog;
